<template>
  <div class="min-h-screen max-w-7xl overflow-auto  py-6">
    <div v-if="loaded && user" class="space-y-6">
      <!-- Header: avatar + name + join date -->
      <section
        class="rounded-2xl bg-gradient-to-br from-[#1b2421] to-[#141a1e] ring-1 ring-[#334D44]/60 p-4 sm:p-6 text-white"
      >
        <div class="flex items-center gap-4">
          <img
            :src="avatarSrc"
            class="w-16 h-16 sm:w-20 sm:h-20 rounded-full ring-2 ring-white/70 shadow-md object-cover bg-[#0f1417]"
            alt="avatar"
          />
          <div class="flex-1">
            <div class="flex items-center gap-2 flex-wrap">
              <h2 class="text-lg sm:text-xl font-extrabold">{{ user.username || 'کاربر' }}</h2>
              <span
                class="rounded-full bg-emerald-500/15 text-emerald-300 text-[11px] sm:text-xs ring-1 ring-emerald-400/30 px-2 py-1"
              >درجه: {{ user.tier || 'معمولی' }}</span>
            </div>
            <p class="mt-1 text-xs sm:text-sm text-white/70">
              عضو از
              <span class="font-semibold text-yellow-300">{{ joinDate }}</span>
            </p>
          </div>

          <div class="hidden sm:flex items-center gap-2">
            <button
              @click="copyLink"
              class="rounded-xl bg-[#b9f1cf] text-[#0a1f14] px-3 py-2 text-sm font-semibold hover:brightness-95"
            >
              کپی لینک
            </button>
            <button
              @click="nativeShare"
              class="rounded-xl bg-[#1e2623] ring-1 ring-[#334D44]/60 px-3 py-2 text-sm hover:bg-[#22302b]"
            >
              اشتراک‌گذاری
            </button>
          </div>
        </div>
      </section>

      <!-- Referral link + quick actions -->
      <section
        class="rounded-2xl bg-[#141a1e] ring-1 ring-[#334D44]/60 p-4 sm:p-6 text-white"
      >
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div class="lg:col-span-8 space-y-3">
            <div class="text-sm text-white/80 font-medium">لینک دعوت شما</div>
            <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input
                :value="inviteLink"
                readonly
                class="flex-1 rounded-xl bg-[#0f1417] ring-1 ring-[#334D44]/50 px-3 py-2 text-sm select-all"
              />
              <div class="flex gap-2">
                <button
                  @click="copyLink"
                  class="rounded-xl bg-[#b9f1cf] text-[#0a1f14] px-4 py-2 text-sm font-semibold hover:brightness-95"
                >
                  کپی
                </button>
                <button
                  @click="nativeShare"
                  class="rounded-xl bg-[#1e2623] ring-1 ring-[#334D44]/60 px-4 py-2 text-sm hover:bg-[#22302b]"
                >
                  اشتراک‌گذاری
                </button>
              </div>
            </div>

            <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 pt-2">
              <a
                :href="`https://wa.me/?text=${encodedShareText}`"
                target="_blank"
                class="rounded-xl bg-[#1e2623] ring-1 ring-[#334D44]/60 px-3 py-2 text-center text-xs hover:bg-[#22302b]"
              >واتس‌اپ</a>
              <a
                :href="`https://t.me/share/url?url=${encodedInvite}&text=${encodedCaption}`"
                target="_blank"
                class="rounded-xl bg-[#1e2623] ring-1 ring-[#334D44]/60 px-3 py-2 text-center text-xs hover:bg-[#22302b]"
              >تلگرام</a>
              <a
                :href="`sms:?&body=${encodedShareText}`"
                class="rounded-xl bg-[#1e2623] ring-1 ring-[#334D44]/60 px-3 py-2 text-center text-xs hover:bg-[#22302b]"
              >پیامک</a>
              <button
                @click="copyLink"
                class="rounded-xl bg-[#1e2623] ring-1 ring-[#334D44]/60 px-3 py-2 text-center text-xs hover:bg-[#22302b]"
              >کپی سریع</button>
            </div>
          </div>

          <div class="lg:col-span-4">
            <div class="rounded-2xl bg-[#0f1417] ring-1 ring-[#334D44]/50 p-4 h-full flex flex-col items-center justify-center gap-3">
              <div class="text-xs text-white/70">اسکن کنید و ثبت‌نام کنید</div>
              <img
                :src="qrSrc"
                alt="QR"
                class="h-40 w-40 rounded-xl ring-1 ring-white/10 bg-white object-contain"
              />
              <div class="text-[11px] text-white/50 text-center leading-5">
                برای تولید QR داخلی از کتابخانه‌ها می‌توانید استفاده کنید؛ این QR از سرویس عمومی تولید شده است.
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Stats -->
      <section class="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div class="rounded-2xl bg-[#141a1e] ring-1 ring-[#334D44]/60 p-4 text-white">
          <div class="text-xs text-white/60">دوستان دعوت‌شده</div>
          <div class="mt-2 text-2xl font-extrabold">{{ user.invitation || 0 }}</div>
        </div>
        <div class="rounded-2xl bg-[#141a1e] ring-1 ring-[#334D44]/60 p-4 text-white">
          <div class="text-xs text-white/60">پاداش‌های دریافتی</div>
          <div class="mt-2 text-2xl font-extrabold">{{ formatMoney(user.gift || 0) }} تومان</div>
        </div>
        <div class="rounded-2xl bg-[#141a1e] ring-1 ring-[#334D44]/60 p-4 text-white">
          <div class="text-xs text-white/60">نرخ تبدیل</div>
          <div class="mt-2 text-2xl font-extrabold">{{ conversionRate }}</div>
        </div>
        <div class="rounded-2xl bg-[#141a1e] ring-1 ring-[#334D44]/60 p-4 text-white">
          <div class="text-xs text-white/60">کد دعوت</div>
          <div class="mt-2 text-2xl font-extrabold ltr">{{ user.refcode || '—' }}</div>
        </div>
      </section>

      <!-- Hints -->
      <section class="rounded-2xl bg-[#0f1417] ring-1 ring-[#334D44]/60 p-4 sm:p-6 text-white">
        <h3 class="font-extrabold text-white text-base sm:text-lg">چطور پاداش بیشتری بگیرم؟</h3>
        <ul class="mt-3 space-y-2 text-sm text-white/80">
          <li class="flex items-center gap-2">
            <span class="inline-block h-2 w-2 rounded-full bg-emerald-400"></span>
            لینک را همراه با توضیح کوتاه بفرستید تا اعتماد مخاطب جلب شود.
          </li>
          <li class="flex items-center gap-2">
            <span class="inline-block h-2 w-2 rounded-full bg-emerald-400"></span>
            از شبکه‌ای که بیشترین تعامل را دارید (مثلا گروه خانوادگی) شروع کنید.
          </li>
          <li class="flex items-center gap-2">
            <span class="inline-block h-2 w-2 rounded-full bg-emerald-400"></span>
            اسکرین‌شات از تجربه مثبت خودتان اضافه کنید.
          </li>
        </ul>
      </section>

      <!-- Big CTA -->
      <section>
        <button
          @click="copyLink"
          class="w-full rounded-2xl bg-gradient-to-r from-emerald-400 to-emerald-500 text-[#0a1f14] font-extrabold text-sm sm:text-base py-3 sm:py-4 hover:opacity-95 shadow-lg"
        >
          دعوت از دوستان
        </button>
      </section>
    </div>

    <div v-else class="flex justify-center items-center py-16">
      <svg class="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
      </svg>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios'
import Swal from 'sweetalert2'
import { ref, computed, onMounted } from 'vue'

const url = process.env.VUE_APP_API_URL || ''

const loaded = ref(false)
const user = ref(null)

const inviteLink = computed(() => {
  const code = user.value?.refcode || ''
  const base = window.location?.origin || ''
  return `${base}/register?ref=${code}`
})

const avatarSrc = computed(() => {
  const u = user.value
  if (u?.avatar) return u.avatar
  const name = encodeURIComponent(u?.username || 'User')
  return `https://ui-avatars.com/api/?name=${name}&background=0D8ABC&color=fff`
})

const joinDate = computed(() => {
  const d = user.value?.date ? new Date(user.value.date) : null
  if (!d || isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('fa-IR')
})

const conversionRate = computed(() => {
  const invited = Number(user.value?.invitation || 0)
  const rewarded = Number(user.value?.gift || 0)
  if (!invited) return '۰٪'
  // فرض: هر دعوت موفق منجر به پاداش > 0
  const success = rewarded > 0 ? Math.min(100, Math.round((rewarded / (invited * 1_000_000)) * 100)) : 0
  return `${success}٪`
})

const encodedInvite = computed(() => encodeURIComponent(inviteLink.value))
const caption = computed(() => 'با لینک من در پرشین گلد ثبت‌نام کن و پاداش بگیر ✨')
const encodedCaption = computed(() => encodeURIComponent(caption.value))
const encodedShareText = computed(() => encodeURIComponent(`${caption.value}\n${inviteLink.value}`))
const qrSrc = computed(() =>
  `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodedInvite.value}`
)

async function loadProfile () {
  try {
    const { data } = await axios.get(`${url}/user/profile`)
    user.value = data?.user || null
  } catch (e) {
    user.value = null
  } finally {
    loaded.value = true
  }
}

function formatMoney(n) {
  if (!isFinite(n)) return '۰'
  return new Intl.NumberFormat('fa-IR').format(Math.round(Number(n)))
}

function copyLink() {
  navigator.clipboard.writeText(inviteLink.value).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'کپی شد!',
      text: 'لینک دعوت در کلیپ‌بورد شماست.',
      confirmButtonText: 'باشه',
      timer: 1800,
      timerProgressBar: true
    })
  })
}

function nativeShare() {
  if (navigator.share) {
    navigator.share({
      title: 'دعوت به پرشین گلد',
      text: caption.value,
      url: inviteLink.value
    }).catch(() => {})
  } else {
    copyLink()
  }
}

onMounted(loadProfile)
</script>

<style scoped>
.ltr { direction: ltr; }
</style>
